import {
  GamePlanEntity,
  GamePlanFormValues,
  GamePlanRequestPayload,
  TodoItemEntity,
  UnitSystemConversionMap,
  Enterprise
} from "@ctra/api";
import { Epoch, TS, Units, UnitSystem } from "@ctra/utils";

/**
 * Convert a game plan to form values for editing
 * @param id
 * @param title
 * @param description
 * @param farmID
 * @param dataDescriptorID
 * @param value
 * @param startEpoch
 * @param endEpoch
 * @param taskList
 */
const toFormValues = ({
  id,
  title,
  description,
  farmID,
  dataDescriptorID,
  goal: { value },
  timePeriod: { startEpoch, endEpoch },
  taskList,
  unitSystem: gamePlanUnitSystem,
  globalUnitSystem
}: Omit<GamePlanEntity, "taskList"> & {
  taskList: TodoItemEntity[];
  globalUnitSystem: UnitSystem;
}): GamePlanFormValues => {
  /**
   * get the possible units of game plan's data descriptor
   */
  const {
    valueProperties: { units }
  } = Enterprise.entities.getDataDescriptor(Enterprise.store.getState(), { id: dataDescriptorID });

  /**
   * Make sure the goal value is according to the globally selected unit system
   */
  const getGoalValue = (): number =>
    Units.convertToUnitSystem(value, units, { from: gamePlanUnitSystem as UnitSystem, to: globalUnitSystem });

  return {
    id,
    title,
    description,
    farmID,
    dataDescriptorID,
    hasTarget: value > 0 ? 1 : 0,
    goal: getGoalValue() || undefined,
    taskList: taskList.map(({ id, status, label, assignedTo, dueDateTs }) => ({
      id,
      status,
      label,
      assignedTo,
      ...(dueDateTs
        ? {
            dueDateMoment: TS.asMoment(dueDateTs)
          }
        : {})
    })),
    timePeriod: [Epoch.asMoment(startEpoch), Epoch.asMoment(endEpoch)],
    unitSystem: gamePlanUnitSystem
  };
};

/**
 * Process the wizard values and create a request payload
 * @param id
 * @param title
 * @param description
 * @param startMoment
 * @param endMoment
 * @param farmID
 * @param dataDescriptorID
 * @param hasTarget
 * @param goal
 * @param taskList
 */
const fromFormValues = ({
  id,
  title,
  description = "",
  timePeriod: [startMoment, endMoment],
  farmID,
  dataDescriptorID,
  hasTarget,
  goal,
  taskList,
  unitSystem
}: GamePlanFormValues): GamePlanRequestPayload => ({
  id,
  title,
  description,
  farmID,
  startEpoch: Number(startMoment),
  endEpoch: Number(endMoment),
  dataDescriptorID,
  goal: {
    value: hasTarget && goal ? goal.toString() : "",
    unitSystem: UnitSystemConversionMap[unitSystem as UnitSystem]
  },
  taskList: taskList.map(({ id, status, label, assignedTo, dueDateMoment }) => ({
    id,
    status,
    label,
    ...(assignedTo
      ? {
          assignedTo
        }
      : {}),
    ...(dueDateMoment
      ? {
          dueDateTs: TS.serialize(dueDateMoment)
        }
      : {})
  }))
});

/**
 * API for converting form values to game plan request payload
 */
export const useConversion = (): [typeof fromFormValues, typeof toFormValues] => [
  fromFormValues,
  toFormValues
];
